import { useEffect, useRef } from 'react';
import axios, { AxiosResponse, isAxiosError } from 'axios';
import { Query, useQuery } from '@tanstack/react-query';
import { queryClient } from '../queryClient';
import { QueryServerKeys } from 'constants/QueryServerKeys';
import { PermissionKey } from 'constants/permissionKeys';
import {
  SESSION_ACCESS_PATH,
  getSessionAccess,
} from 'services/sessionAccess';
import { SessionAccess, meResponse } from 'types/user';
import { keysGained, keysLost, sameAccess } from 'helpers/sessionAccess';

const POLL_MS = 3 * 60 * 1000;
const TICK_MS = 30 * 1000;
const IDLE_AFTER_MS = 5 * 60 * 1000;
const FORBIDDEN_DELAY_MS = 1000;
const FORBIDDEN_RECHECK_GAP_MS = 60 * 1000;
const ACTIVITY_EVENTS = [
  'pointerdown',
  'pointermove',
  'keydown',
  'wheel',
  'touchstart',
];

const ACCESS_KEY = [QueryServerKeys.USER.ACCESS];
const USER_KEY = [QueryServerKeys.USER.GET_USER_DATA];
const SESSION_KEYS: unknown[] = [
  QueryServerKeys.USER.GET_USER_DATA,
  QueryServerKeys.USER.ACCESS,
];
const NO_ACCESS = { roles: '', permissions: [] as PermissionKey[] };

let freshNext = false;

export const recheckAccess = () => {
  freshNext = true;
  return queryClient.invalidateQueries({ queryKey: ACCESS_KEY });
};

const isSessionQuery = ({ queryKey }: Query) =>
  SESSION_KEYS.includes(queryKey[0]);

const statusOf = (error: unknown) =>
  isAxiosError(error) ? error.response?.status : undefined;

const retryCheck = (failureCount: number, error: unknown) => {
  const status = statusOf(error);
  if (status === 401) return failureCount < 1;
  if (status && status < 500) return false;
  return failureCount < 2;
};

const sessionUser = () =>
  queryClient.getQueryData<AxiosResponse<meResponse>>(USER_KEY)?.data?.user;

const accessFromSession = (): SessionAccess | undefined => {
  const user = sessionUser();
  const version = user?.access?.version;
  if (!user || !version) return undefined;
  return {
    version,
    roles: user.roles ?? '',
    permissions: user.access.permissions,
  };
};

const patchSession = ({
  roles,
  permissions,
}: Pick<SessionAccess, 'roles' | 'permissions'>) =>
  queryClient.setQueryData<AxiosResponse<meResponse>>(
    USER_KEY,
    result =>
      result && {
        ...result,
        data: {
          ...result.data,
          user: {
            ...result.data.user,
            roles,
            access: { ...result.data.user.access, permissions },
          },
        },
      },
  );

export function useAccessSync({
  enabled,
  permissions,
}: {
  enabled: boolean;
  permissions?: readonly PermissionKey[];
}) {
  const heldPermissions = useRef(permissions);
  const lastCheckAt = useRef(0);
  const lastActiveAt = useRef(Date.now());
  const forbiddenTimer = useRef<ReturnType<typeof setTimeout>>();
  const lastForbiddenCheckAt = useRef(0);

  const { data, error, refetch } = useQuery({
    queryKey: ACCESS_KEY,
    queryFn: async () => {
      const fresh = freshNext;
      freshNext = false;
      lastCheckAt.current = Date.now();
      const known = queryClient.getQueryData<SessionAccess>(ACCESS_KEY);
      const answeredByMe = !known && !fresh ? accessFromSession() : undefined;
      if (answeredByMe) return answeredByMe;
      const current = await getSessionAccess({ version: known?.version, fresh });
      if (current) return current;
      if (known) return known;
      throw new Error('Access answered not modified before it was ever read');
    },
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: retryCheck,
  });

  useEffect(() => {
    const user = sessionUser();
    if (!data || !user) return;
    if (sameAccess(data, { roles: user.roles, permissions: user.access?.permissions })) return;
    patchSession(data);
  }, [data]);

  useEffect(() => {
    const status = statusOf(error);
    if (status === 401 || status === 403) patchSession(NO_ACCESS);
  }, [error]);

  useEffect(() => {
    const before = heldPermissions.current;
    heldPermissions.current = permissions;
    if (!before || !permissions || before === permissions) return;
    if (keysLost(before, permissions).length) {
      const predicate = (query: Query) => !isSessionQuery(query);
      queryClient.removeQueries({ type: 'inactive', predicate });
      queryClient.resetQueries({ type: 'active', predicate });
    } else if (keysGained(before, permissions).length) {
      queryClient.refetchQueries({
        type: 'active',
        predicate: query =>
          !isSessionQuery(query) && query.state.status === 'error',
      });
    }
  }, [permissions]);

  useEffect(() => {
    if (!enabled) return;
    const isActive = () =>
      document.visibilityState === 'visible' &&
      Date.now() - lastActiveAt.current < IDLE_AFTER_MS;
    const checkIfDue = () => {
      if (!isActive() || Date.now() - lastCheckAt.current < POLL_MS) return;
      refetch({ cancelRefetch: false });
    };
    const markActive = () => {
      if (document.visibilityState !== 'visible') return;
      lastActiveAt.current = Date.now();
      checkIfDue();
    };
    const tick = setInterval(checkIfDue, TICK_MS);
    ACTIVITY_EVENTS.forEach(event =>
      window.addEventListener(event, markActive, { passive: true }),
    );
    document.addEventListener('visibilitychange', markActive);
    window.addEventListener('focus', markActive);
    return () => {
      clearInterval(tick);
      ACTIVITY_EVENTS.forEach(event =>
        window.removeEventListener(event, markActive),
      );
      document.removeEventListener('visibilitychange', markActive);
      window.removeEventListener('focus', markActive);
    };
  }, [enabled, refetch]);

  useEffect(() => {
    if (!enabled) return;
    const checkAfterForbidden = () => {
      if (forbiddenTimer.current) return;
      const wait = Math.max(
        FORBIDDEN_DELAY_MS,
        lastForbiddenCheckAt.current + FORBIDDEN_RECHECK_GAP_MS - Date.now(),
      );
      forbiddenTimer.current = setTimeout(() => {
        forbiddenTimer.current = undefined;
        lastForbiddenCheckAt.current = Date.now();
        recheckAccess();
      }, wait);
    };
    const interceptor = axios.interceptors.response.use(undefined, error => {
      const isAccessCheck = error?.config?.url?.endsWith(SESSION_ACCESS_PATH);
      const isVisible = document.visibilityState === 'visible';
      if (statusOf(error) === 403 && !isAccessCheck && isVisible)
        checkAfterForbidden();
      return Promise.reject(error);
    });
    return () => {
      axios.interceptors.response.eject(interceptor);
      clearTimeout(forbiddenTimer.current);
      forbiddenTimer.current = undefined;
    };
  }, [enabled]);
}
